import React, { FC, ReactNode } from 'react';
import Head from 'next/head';
import { ContextProps as CP, useAppContext } from '../../store/appContext';
import { CanonicalUrl, getCanonicalUrl } from '../../helpers/seoHelper';
import { generateAlternateURLs } from '../../helpers/alternateURLs';
import { Scripts } from './Scripts';
import { Icons } from './Icons';
import { PageSpecificMetadata, PageSpecificMetadataProps } from '../PageSpecificMetadata/PageSpecificMetadata';
import { GeneralMetadata } from './GeneralMetadata';
import { useUrlBuilder } from '../../hooks/useUrlBuilder';

interface HeadWrapperProps {
  metadata?: PageSpecificMetadataProps['metadata'];
}

const renderAlternateURLs = (
  countries: CP['countriesForSeo'],
  location: CP['location'],
  canonicalUrl: string,
  exclusions: CP['productDetails']['exclusions'],
  countryCode: CP['countryCode'],
): ReactNode => {
  return (
    <Head>
      {generateAlternateURLs(countries, location, canonicalUrl, exclusions, countryCode).map(el => {
        return <link key={el.hrefLang} href={el.href} hrefLang={el.hrefLang} rel="alternate" />;
      })}
    </Head>
  );
};

const getCanonical = (location: CP['location']): CanonicalUrl => {
  const { hostname, query, path } = location;
  return getCanonicalUrl(hostname, path, query);
};

export const HeadWrapper: FC<HeadWrapperProps> = ({ metadata }) => {
  const {
    country,
    countriesForSeo,
    countryCode,
    email,
    location,
    noindex,
    overridingCanonicalUrl,
    productDetails,
  } = useAppContext();
  const { exclusions } = productDetails || {};
  const { hostname, originalUrl, query } = location;
  const { localized } = useUrlBuilder();

  const { fullUrl, relativeUrl } = overridingCanonicalUrl
    ? getCanonicalUrl(hostname, localized(overridingCanonicalUrl), query)
    : getCanonical(location);
  const original = `https://${hostname}${originalUrl}`;
  const isCanonical = fullUrl === original;

  return (
    <>
      <GeneralMetadata canonicalUrl={fullUrl} country={country} noindex={noindex} />
      <PageSpecificMetadata canonicalUrl={fullUrl} metadata={metadata} />
      {isCanonical ? renderAlternateURLs(countriesForSeo, location, relativeUrl, exclusions, countryCode) : null}
      <Icons />
      <Scripts email={email} />
    </>
  );
};

export default HeadWrapper;
